import { useState, useEffect } from "react";



// ── CLOSd Brand Tokens ───────────────────────────────────────────────────────
const C = {
  navyDeep:    "#f4f5f7",
  navyDark:    "#f4f5f7",
  navyMid:     "#e8eaed",
  navyBorder:  "#d0d4da",
  navyLight:   "#e8eaed",

  green:       "#75BB2E",
  greenDim:    "#5a9a1e",
  greenFaint:  "rgba(117,187,46,0.1)",
  greenBorder: "rgba(117,187,46,0.35)",
  greenChip:   "rgba(117,187,46,0.1)",

  red:         "#dc2626",
  redFaint:    "rgba(220,38,38,0.08)",

  textPrimary: "#071D39",
  textMuted:   "#516469",
  textDim:     "#89A8B1",
};

const fmt = (val) =>
  new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(val || 0);

// ─── Currency Input ──────────────────────────────────────────────────────────
function CurrencyInput({ label, sublabel, value, onChange, readOnly }) {
  const [focused, setFocused] = useState(false);

  const handleChange = (e) => {
    const raw = e.target.value.replace(/[^0-9.]/g, "");
    onChange(parseFloat(raw) || 0);
  };

  return (
    <div style={s.fieldRow}>
      <div style={s.fieldLabels}>
        <span style={s.fieldLabel}>{label}</span>
        {sublabel && <span style={s.fieldSublabel}>{sublabel}</span>}
      </div>
      {readOnly ? (
        <div style={s.calcChip}>{fmt(value)}</div>
      ) : (
        <div style={{
          ...s.inputBox,
          ...(focused ? s.inputBoxFocused : {}),
        }}>
          <span style={s.prefix}>$</span>
          <input
            style={s.input}
            value={value === 0 ? "" : String(Math.round(value))}
            onChange={handleChange}
            onFocus={() => setFocused(true)}
            onBlur={() => setFocused(false)}
            placeholder="0"
            inputMode="numeric"
          />
        </div>
      )}
    </div>
  );
}

// ─── Section Block ───────────────────────────────────────────────────────────
function Section({ title, children }) {
  return (
    <div style={s.section}>
      <div style={s.sectionHeader}>
        <span style={s.sectionTitle}>{title}</span>
        <div style={s.sectionLine} />
      </div>
      {children}
    </div>
  );
}

// ─── Seller Net Sheet ────────────────────────────────────────────────────────
function SellerNetSheet() {
  const [salePrice, setSalePrice]         = useState(385000);
  const [commissionRate, setCommission]   = useState(0.05);
  const [titleSearchFee, setTitleSearch]  = useState(550);
  const [agentServices, setAgentServices] = useState(9900);
  const [homeWarranty, setHomeWarranty]   = useState(0);
  const [miscExp, setMiscExp]             = useState(0);
  const [firstMortgage, setFirst]         = useState(251104);
  const [secondMortgage, setSecond]       = useState(0);
  const [miscPayoffs, setMiscPayoffs]     = useState(0);

  const docStamps      = salePrice * 0.007;
  const titleInsurance = salePrice * 0.005;
  const commission     = salePrice * commissionRate;
  const totalExpenses  = docStamps + titleInsurance + commission + titleSearchFee + agentServices + homeWarranty + miscExp;
  const totalPayoffs   = firstMortgage + secondMortgage + miscPayoffs;
  const netToSeller    = salePrice - totalExpenses - totalPayoffs;
  const positive       = netToSeller >= 0;

  return (
    <div style={s.card}>
      {/* Header */}
      <div style={{ ...s.cardTop, background: `linear-gradient(135deg, #e8eaed 0%, #d8dde5 100%)` }}>
        <div style={s.cardTopInner}>
          <div style={s.cardIconWrap}>
            <span style={{ fontSize: 22 }}>🏠</span>
          </div>
          <div>
            <div style={s.cardName}>Seller Net Sheet</div>
            <div style={s.cardDesc}>Estimated proceeds at closing</div>
          </div>
        </div>
        <div style={s.headerResult}>
          <div style={s.headerResultLabel}>Est. Net to Seller</div>
          <div style={{ ...s.headerResultValue, color: positive ? C.green : C.red }}>
            {fmt(netToSeller)}
          </div>
        </div>
      </div>

      <div style={s.cardBody}>
        {/* Sale Details */}
        <Section title="Sale Details">
          <CurrencyInput label="Estimated Sale Price" value={salePrice} onChange={setSalePrice} />

          <div style={s.fieldRow}>
            <div style={s.fieldLabels}>
              <span style={s.fieldLabel}>Commission Rate</span>
              <span style={s.fieldSublabel}>Applied to sale price</span>
            </div>
            <div style={s.commissionGroup}>
              {[0.04, 0.045, 0.05, 0.055, 0.06].map((r) => (
                <button
                  key={r}
                  onClick={() => setCommission(r)}
                  style={{ ...s.pctBtn, ...(commissionRate === r ? s.pctBtnActive : {}) }}
                >
                  {(r * 100).toFixed(1)}%
                </button>
              ))}
            </div>
          </div>
          <div style={s.commissionPreview}>
            Commission at {(commissionRate * 100).toFixed(1)}%:{" "}
            <strong style={{ color: C.green }}>{fmt(commission)}</strong>
          </div>
        </Section>

        {/* Closing Costs */}
        <Section title="Closing Costs">
          <CurrencyInput label="Doc Stamps on Deed"     sublabel="sale price × 0.70% — auto-calculated"                        value={docStamps}      onChange={() => {}} readOnly />
          <CurrencyInput label="Owner's Title Insurance" sublabel="sale price × 0.50% — auto-calculated"                       value={titleInsurance} onChange={() => {}} readOnly />
          <CurrencyInput label="Commission"              sublabel={`${(commissionRate*100).toFixed(1)}% of sale price — auto`}  value={commission}     onChange={() => {}} readOnly />
          <CurrencyInput label="Title Search / Closing Fee"           value={titleSearchFee} onChange={setTitleSearch}  />
          <CurrencyInput label="Agent Services & Marketing"           value={agentServices}  onChange={setAgentServices} />
          <CurrencyInput label="Home Warranty"                        value={homeWarranty}   onChange={setHomeWarranty}  />
          <CurrencyInput label="Misc Expenses (seller-paid costs)"    value={miscExp}        onChange={setMiscExp}       />
        </Section>

        {/* Payoffs */}
        <Section title="Payoffs">
          <CurrencyInput label="First Mortgage Pay-Off"  value={firstMortgage}  onChange={setFirst}        />
          <CurrencyInput label="Second Mortgage Pay-Off" value={secondMortgage} onChange={setSecond}       />
          <CurrencyInput label="Misc Payoffs"            value={miscPayoffs}    onChange={setMiscPayoffs}  />
        </Section>

        {/* Summary */}
        <div style={s.summary}>
          <div style={s.summaryRow}>
            <span style={s.summaryLabel}>Sale Price</span>
            <span style={s.summaryVal}>{fmt(salePrice)}</span>
          </div>
          <div style={s.summaryRow}>
            <span style={s.summaryLabel}>− Total Closing Costs</span>
            <span style={{ ...s.summaryVal, color: C.red }}>({fmt(totalExpenses)})</span>
          </div>
          <div style={s.summaryRow}>
            <span style={s.summaryLabel}>− Total Payoffs</span>
            <span style={{ ...s.summaryVal, color: C.red }}>({fmt(totalPayoffs)})</span>
          </div>
          <div style={s.summaryDivider} />
          <div style={s.summaryRow}>
            <span style={s.summaryBigLabel}>Approximate Net to Seller</span>
            <span style={{ ...s.summaryBigVal, color: positive ? C.green : C.red }}>
              {fmt(netToSeller)}
            </span>
          </div>
        </div>

        <p style={s.disclaimer}>Estimate only. Actual figures vary. Consult your closing agent for final numbers.</p>
      </div>
    </div>
  );
}

// ─── Buyer Cash to Close ─────────────────────────────────────────────────────
function BuyerCTC() {
  const [salePrice, setSalePrice]               = useState(235000);
  const [loanAmount, setLoanAmount]             = useState(235000);
  const [lenderTitle, setLenderTitle]           = useState(200);
  const [titleSearch, setTitleSearch]           = useState(100);
  const [upfrontHOI, setUpfrontHOI]             = useState(1500);
  const [prepaidInterest, setPrepaidInterest]   = useState(0);
  const [prepaidTaxes, setPrepaidTaxes]         = useState(0);
  const [prepaidHOI, setPrepaidHOI]             = useState(0);
  const [survey, setSurvey]                     = useState(350);
  const [recordingFees, setRecordingFees]       = useState(200);
  const [appraisal, setAppraisal]               = useState(0);
  const [discountPoints, setDiscountPoints]     = useState(0);
  const [loanFees, setLoanFees]                 = useState(0);
  const [sellerConcessions, setSellerC]         = useState(0);
  const [lenderConcessions, setLenderC]         = useState(0);

  const docStamps        = salePrice * 0.0035;
  const totalExpenses    = docStamps + lenderTitle + titleSearch + upfrontHOI + prepaidInterest +
                           prepaidTaxes + prepaidHOI + survey + recordingFees + appraisal +
                           discountPoints + loanFees;
  const totalConcessions = sellerConcessions + lenderConcessions;
  const cashToClose      = salePrice + totalExpenses - totalConcessions - loanAmount;
  const downPayment      = salePrice - loanAmount;
  const downPct          = salePrice > 0 ? ((downPayment / salePrice) * 100).toFixed(1) : "0.0";

  return (
    <div style={s.card}>
      {/* Header — same navy gradient, green accents */}
      <div style={{ ...s.cardTop, background: `linear-gradient(135deg, #e8eaed 0%, #d8dde5 100%)` }}>
        <div style={s.cardTopInner}>
          <div style={s.cardIconWrap}>
            <span style={{ fontSize: 22 }}>🔑</span>
          </div>
          <div>
            <div style={s.cardName}>Buyer Cash to Close</div>
            <div style={s.cardDesc}>Estimated funds needed at closing</div>
          </div>
        </div>
        <div style={s.headerResult}>
          <div style={s.headerResultLabel}>Est. Cash to Close</div>
          <div style={{ ...s.headerResultValue, color: cashToClose >= 0 ? C.green : C.red }}>
            {fmt(cashToClose)}
          </div>
        </div>
      </div>

      <div style={s.cardBody}>
        {/* Loan Details */}
        <Section title="Loan Details">
          <CurrencyInput label="Estimated Sale Price" value={salePrice}   onChange={setSalePrice}  />
          <CurrencyInput label="Loan Amount"          value={loanAmount}  onChange={setLoanAmount} />
          <div style={s.downPaymentBar}>
            <div>
              <div style={s.downPayLabel}>Down Payment</div>
              <div style={s.downPayAmount}>{fmt(downPayment)}</div>
            </div>
            <div style={s.downPayPctBadge}>{downPct}%</div>
          </div>
        </Section>

        {/* Closing Costs */}
        <Section title="Closing Costs">
          <CurrencyInput label="Doc Stamps on Mortgage"        sublabel="sale price × 0.35% — auto-calculated" value={docStamps}      onChange={() => {}} readOnly />
          <CurrencyInput label="Lender's Title Insurance"      value={lenderTitle}    onChange={setLenderTitle}    />
          <CurrencyInput label="Title Search / Closing Fee"    value={titleSearch}    onChange={setTitleSearch}    />
          <CurrencyInput label="Survey"                        value={survey}         onChange={setSurvey}         />
          <CurrencyInput label="Recording Fees"                value={recordingFees}  onChange={setRecordingFees}  />
          <CurrencyInput label="Appraisal"                     value={appraisal}      onChange={setAppraisal}      />
          <CurrencyInput label="Discount Points"               value={discountPoints} onChange={setDiscountPoints} />
          <CurrencyInput label="Loan Fees (credit, flood, UW)" value={loanFees}      onChange={setLoanFees}       />
        </Section>

        {/* Prepaids */}
        <Section title="Prepaids">
          <CurrencyInput label="Upfront Homeowner's Insurance" value={upfrontHOI}      onChange={setUpfrontHOI}      />
          <CurrencyInput label="Prepaid Interest"              value={prepaidInterest} onChange={setPrepaidInterest} />
          <CurrencyInput label="Prepaid Taxes"                 value={prepaidTaxes}    onChange={setPrepaidTaxes}    />
          <CurrencyInput label="Prepaid HOI (escrow)"          value={prepaidHOI}      onChange={setPrepaidHOI}      />
        </Section>

        {/* Concessions */}
        <Section title="Concessions">
          <CurrencyInput label="Seller Concessions" value={sellerConcessions} onChange={setSellerC} />
          <CurrencyInput label="Lender Concessions" value={lenderConcessions} onChange={setLenderC} />
        </Section>

        {/* Summary */}
        <div style={s.summary}>
          <div style={s.summaryRow}>
            <span style={s.summaryLabel}>Sale Price</span>
            <span style={s.summaryVal}>{fmt(salePrice)}</span>
          </div>
          <div style={s.summaryRow}>
            <span style={s.summaryLabel}>+ Total Closing Costs</span>
            <span style={s.summaryVal}>{fmt(totalExpenses)}</span>
          </div>
          <div style={s.summaryRow}>
            <span style={s.summaryLabel}>− Loan Amount</span>
            <span style={{ ...s.summaryVal, color: C.red }}>({fmt(loanAmount)})</span>
          </div>
          {totalConcessions > 0 && (
            <div style={s.summaryRow}>
              <span style={s.summaryLabel}>− Concessions</span>
              <span style={{ ...s.summaryVal, color: C.green }}>({fmt(totalConcessions)})</span>
            </div>
          )}
          <div style={s.summaryDivider} />
          <div style={s.summaryRow}>
            <span style={s.summaryBigLabel}>Approx. Cash to Close</span>
            <span style={{ ...s.summaryBigVal, color: cashToClose >= 0 ? C.green : C.red }}>
              {fmt(cashToClose)}
            </span>
          </div>
        </div>

        <p style={s.disclaimer}>Estimate only. Actual figures vary. Consult your lender and closing agent for final numbers.</p>
      </div>
    </div>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────
export default function NetSheetCalculator() {
  const [tab, setTab] = useState("seller");
const [windowWidth, setWindowWidth] = useState(window.innerWidth);

useEffect(() => {
  const handleResize = () => {
    setWindowWidth(window.innerWidth);
    if (window.innerWidth < 768 && tab === "both") setTab("seller");
  };
  window.addEventListener("resize", handleResize);
  return () => window.removeEventListener("resize", handleResize);
}, [tab]);

  return (
    <div style={s.page}>
      {/* Page Header */}
      <div style={s.pageHeader}>
        <div style={{ fontSize: 28 }}>🧮</div>
        <div>
          <h1 style={s.pageTitle}>Net Sheet Calculator</h1>
          <p style={s.pageSubtitle}>Estimate proceeds & closing costs in seconds</p>
        </div>
      </div>

      {/* Tabs */}
      <div style={s.tabBar}>
        {[
          { id: "seller", label: "🏠  Seller Net"    },
          { id: "buyer",  label: "🔑  Buyer CTC"     },
          ...(windowWidth >= 768 ? [{ id: "both", label: "⇄  Side by Side" }] : []),
        ].map(({ id, label }) => (
          <button
            key={id}
            onClick={() => setTab(id)}
            style={{ ...s.tabBtn, ...(tab === id ? s.tabBtnActive : {}) }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Calculators */}
      <div style={tab === "both" ? s.content : { padding: "0 28px" }}>
        {(tab === "seller" || tab === "both") && <SellerNetSheet />}
        {(tab === "buyer"  || tab === "both") && <BuyerCTC />}
      </div>
    </div>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────
const s = {
  page: {
    background: C.navyDeep,
    fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
    paddingBottom: 60,
  },
  pageHeader: {
    padding: "28px 28px 20px",
    display: "flex",
    alignItems: "center",
    gap: 16,
    borderBottom: `1px solid ${C.navyBorder}`,
  },
  logo: {
    height: 36,
    width: "auto",
    objectFit: "contain",
  },
  pageTitle: {
    margin: 0,
    fontSize: 22,
    fontWeight: 700,
    color: C.textPrimary,
    letterSpacing: "-0.3px",
  },
  pageSubtitle: {
    margin: "3px 0 0",
    fontSize: 13,
    color: C.textDim,
  },
  tabBar: {
    display: "flex",
    gap: 6,
    padding: "16px 28px",
  },
  tabBtn: {
    padding: "9px 18px",
    borderRadius: 8,
    border: `1px solid ${C.navyBorder}`,
    background: "transparent",
    color: C.textMuted,
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    fontFamily: "inherit",
    transition: "all 0.15s ease",
  },
  tabBtnActive: {
    background: C.greenChip,
    border: `1px solid ${C.greenBorder}`,
    color: C.green,
    fontWeight: 700,
  },
  content: {
    display: "flex",
    gap: 20,
    padding: "0 28px",
    justifyContent: "center",
    alignItems: "flex-start",
    flexWrap: "wrap",
  },

  // ── Card ──────────────────────────────────────────────────────────────────
  card: {
    background: C.navyDark,
    border: `1px solid ${C.navyBorder}`,
    borderRadius: 16,
    overflow: "hidden",
    width: "100%",
    maxWidth: 680,
    flex: "1 1 400px",
    minWidth: 0,
    marginBottom: 20,
    boxShadow: "0 4px 20px rgba(0,0,0,0.1)",
  },
  cardTop: {
    padding: "22px 24px 20px",
    display: "flex",
    alignItems: "flex-start",
    justifyContent: "space-between",
    borderBottom: `1px solid ${C.navyBorder}`,
  },
  cardTopInner: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  cardIconWrap: {
    width: 42,
    height: 42,
    borderRadius: 10,
    background: C.greenChip,
    border: `1px solid ${C.greenBorder}`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  },
  cardName: {
    fontSize: 16,
    fontWeight: 700,
    color: C.textPrimary,
    letterSpacing: "-0.2px",
  },
  cardDesc: {
    fontSize: 12,
    color: C.textMuted,
    marginTop: 2,
  },
  headerResult: {
    textAlign: "right",
  },
  headerResultLabel: {
    fontSize: 10,
    textTransform: "uppercase",
    letterSpacing: "1px",
    color: C.textDim,
    fontWeight: 600,
    marginBottom: 4,
  },
  headerResultValue: {
    fontSize: 26,
    fontWeight: 800,
    letterSpacing: "-0.5px",
    fontVariantNumeric: "tabular-nums",
  },
  cardBody: {
    padding: "4px 0 8px",
  },

  // ── Section ───────────────────────────────────────────────────────────────
  section: {
    padding: "18px 24px",
    borderBottom: `1px solid ${C.navyBorder}`,
  },
  sectionHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 14,
  },
  sectionTitle: {
    fontSize: 10,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "1.5px",
    color: C.green,
    opacity: 0.65,
    whiteSpace: "nowrap",
  },
  sectionLine: {
    flex: 1,
    height: 1,
    background: C.navyBorder,
  },

  // ── Fields ────────────────────────────────────────────────────────────────
  fieldRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: 10,
    gap: 12,
  },
  fieldLabels: {
    flex: 1,
    minWidth: 0,
  },
  fieldLabel: {
    fontSize: 13,
    color: C.textMuted,
    display: "block",
    fontWeight: 400,
  },
  fieldSublabel: {
    fontSize: 10,
    color: C.textDim,
    display: "block",
    marginTop: 2,
    fontStyle: "italic",
  },
  inputBox: {
    display: "flex",
    alignItems: "center",
    background: C.navyLight,
    border: `1px solid ${C.navyBorder}`,
    borderRadius: 8,
    overflow: "hidden",
    minWidth: 130,
    transition: "border-color 0.15s, background 0.15s",
  },
  inputBoxFocused: {
    borderColor: C.greenBorder,
    background: "rgba(126,255,160,0.05)",
  },
  prefix: {
    padding: "8px 6px 8px 10px",
    fontSize: 13,
    color: C.textDim,
    fontWeight: 500,
    userSelect: "none",
  },
  input: {
    background: "transparent",
    border: "none",
    outline: "none",
    padding: "8px 10px 8px 2px",
    fontSize: 14,
    fontWeight: 500,
    color: C.textPrimary,
    width: 100,
    textAlign: "right",
    fontFamily: "inherit",
    fontVariantNumeric: "tabular-nums",
  },
  calcChip: {
    padding: "7px 12px",
    background: C.greenChip,
    border: `1px solid ${C.greenBorder}`,
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 600,
    color: C.green,
    minWidth: 130,
    textAlign: "right",
    fontVariantNumeric: "tabular-nums",
  },

  // ── Commission picker ─────────────────────────────────────────────────────
  commissionGroup: {
    display: "flex",
    gap: 4,
    flexWrap: "wrap",
    justifyContent: "flex-end",
  },
  pctBtn: {
    padding: "6px 9px",
    borderRadius: 6,
    border: `1px solid ${C.navyBorder}`,
    background: "transparent",
    color: C.textDim,
    fontSize: 12,
    cursor: "pointer",
    fontFamily: "inherit",
    fontWeight: 500,
    transition: "all 0.12s",
  },
  pctBtnActive: {
    background: C.greenChip,
    border: `1px solid ${C.greenBorder}`,
    color: C.green,
    fontWeight: 700,
  },
  commissionPreview: {
    fontSize: 12,
    color: C.textDim,
    textAlign: "right",
    marginTop: -4,
    marginBottom: 4,
    fontStyle: "italic",
  },

  // ── Down Payment bar ──────────────────────────────────────────────────────
  downPaymentBar: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    background: C.greenChip,
    border: `1px solid ${C.greenBorder}`,
    borderRadius: 8,
    padding: "10px 14px",
    marginTop: 8,
  },
  downPayLabel: {
    fontSize: 10,
    color: C.green,
    opacity: 0.7,
    textTransform: "uppercase",
    letterSpacing: "0.8px",
    fontWeight: 700,
    marginBottom: 2,
  },
  downPayAmount: {
    fontSize: 16,
    fontWeight: 700,
    color: C.green,
    fontVariantNumeric: "tabular-nums",
  },
  downPayPctBadge: {
    fontSize: 22,
    fontWeight: 800,
    color: C.green,
    opacity: 0.55,
    fontVariantNumeric: "tabular-nums",
  },

  // ── Summary box ───────────────────────────────────────────────────────────
  summary: {
    margin: "16px 24px 8px",
    background: C.greenFaint,
    border: `1px solid ${C.greenBorder}`,
    borderRadius: 12,
    padding: "16px",
  },
  summaryRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "baseline",
    padding: "4px 0",
  },
  summaryLabel: {
    fontSize: 13,
    color: C.textMuted,
  },
  summaryVal: {
    fontSize: 14,
    color: C.textPrimary,
    fontWeight: 500,
    fontVariantNumeric: "tabular-nums",
  },
  summaryDivider: {
    height: 1,
    background: C.navyBorder,
    margin: "10px 0",
  },
  summaryBigLabel: {
    fontSize: 14,
    fontWeight: 700,
    color: C.textPrimary,
  },
  summaryBigVal: {
    fontSize: 28,
    fontWeight: 800,
    letterSpacing: "-0.5px",
    fontVariantNumeric: "tabular-nums",
  },

  disclaimer: {
    fontSize: 10,
    color: C.textDim,
    textAlign: "center",
    padding: "8px 24px 16px",
    fontStyle: "italic",
    margin: 0,
    lineHeight: 1.5,
  },
};
